import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { StylingProvider } from "@/context/StylingContext";
import { CompanyProvider } from "@/context/CompanyContext";

import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UsersList from "@/pages/UsersList";
import UserForm from "@/pages/UserForm";
import Settings from "@/pages/Settings";
import CompanyProfile from "@/pages/CompanyProfile";
import TemplatesSettings from "@/pages/TemplatesSettings";
import SmtpSettings from "@/pages/SmtpSettings";
import RolesList from "@/pages/RolesList";
import RoleEdit from "@/pages/RoleEdit";
import SchedulingDefaults from "@/pages/SchedulingDefaults";
import ChecklistDefaults from "@/pages/ChecklistDefaults";
import SecurityEvents from "@/pages/SecurityEvents";
import Branding from "@/pages/Branding";

import CustomersList from "@/pages/CustomersList";
import CustomerForm from "@/pages/CustomerForm";
import CustomerDetail from "@/pages/CustomerDetail";

import SitesList from "@/pages/SitesList";
import SiteForm from "@/pages/SiteForm";
import SiteDetail from "@/pages/SiteDetail";

import MachinesList from "@/pages/MachinesList";
import MachineForm from "@/pages/MachineForm";
import MachineDetail from "@/pages/MachineDetail";

import MachineTypesList from "@/pages/MachineTypesList";
import ContactRolesPage from "@/pages/ContactRolesPage";
import MachineTypeForm from "@/pages/MachineTypeForm";
import MachineTypeDetail from "@/pages/MachineTypeDetail";

import JobsList from "@/pages/JobsList";
import JobForm from "@/pages/JobForm";
import JobDetail from "@/pages/JobDetail";
import JobReportPage from "@/pages/JobReportPage";
import DispatchBoard from "@/pages/DispatchBoard";
import EngineerJobsList from "@/pages/EngineerJobsList";
import EngineerJobDetail from "@/pages/EngineerJobDetail";

import DueSoonPage from "@/pages/DueSoonPage";
import CallToBookPage from "@/pages/CallToBookPage";
import ServiceSchedulesPage from "@/pages/ServiceSchedulesPage";
import ServiceScheduleForm from "@/pages/ServiceScheduleForm";

import EngineersReport from "@/pages/reports/EngineersReport";
import TimeOnSiteReport from "@/pages/reports/TimeOnSiteReport";
import ActionsOutstandingReport from "@/pages/reports/ActionsOutstandingReport";
import CustomerHistoryReport from "@/pages/reports/CustomerHistoryReport";
import MachinesFrequencyReport from "@/pages/reports/MachinesFrequencyReport";

import "@/App.css";

const R = (perm, el) => <ProtectedRoute permission={perm}>{el}</ProtectedRoute>;

export default function App() {
  return (
    <ThemeProvider>
      <StylingProvider>
        <TooltipProvider delayDuration={300} skipDelayDuration={100}>
          <BrowserRouter>
            <CompanyProvider>
              <AuthProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/"
                  element={<ProtectedRoute><AppShell /></ProtectedRoute>}
                >
                  <Route index element={<Dashboard />} />

                  {/* Users */}
                  <Route path="users" element={R("users.read", <UsersList />)} />
                  <Route path="users/new" element={R("users.create", <UserForm />)} />
                  <Route path="users/:id/edit" element={R("users.update", <UserForm />)} />

                  {/* Customers */}
                  <Route path="customers" element={R("customer.read", <CustomersList />)} />
                  <Route path="customers/new" element={R("customer.write", <CustomerForm />)} />
                  <Route path="customers/:id" element={R("customer.read", <CustomerDetail />)} />
                  <Route path="customers/:id/edit" element={R("customer.write", <CustomerForm />)} />

                  {/* Sites */}
                  <Route path="sites" element={R("site.read", <SitesList />)} />
                  <Route path="sites/new" element={R("site.write", <SiteForm />)} />
                  <Route path="sites/:id" element={R("site.read", <SiteDetail />)} />
                  <Route path="sites/:id/edit" element={R("site.write", <SiteForm />)} />

                  {/* Machines */}
                  <Route path="machines" element={R("machine.read", <MachinesList />)} />
                  <Route path="machines/new" element={R("machine.write", <MachineForm />)} />
                  <Route path="machines/:id" element={R("machine.read", <MachineDetail />)} />
                  <Route path="machines/:id/edit" element={R("machine.write", <MachineForm />)} />

                  {/* Machine Types */}
                  <Route path="machine-types" element={R("machine_type.read", <MachineTypesList />)} />
                  <Route path="settings/contact-roles" element={R("settings.contact_roles.read", <ContactRolesPage />)} />
                  <Route path="machine-types/new" element={R("machine_type.write", <MachineTypeForm />)} />
                  <Route path="machine-types/:id" element={R("machine_type.read", <MachineTypeDetail />)} />
                  <Route path="machine-types/:id/edit" element={R("machine_type.write", <MachineTypeForm />)} />

                  {/* Jobs */}
                  <Route path="jobs" element={R("job.read", <JobsList />)} />
                  <Route path="jobs/new" element={R("job.write", <JobForm />)} />
                  <Route path="jobs/:id" element={R("job.read", <JobDetail />)} />
                  <Route path="jobs/:id/edit" element={R("job.write", <JobForm />)} />
                  <Route path="jobs/:id/report" element={R("job.read", <JobReportPage scope="office" />)} />
                  <Route path="dispatch" element={R("dispatch.read", <DispatchBoard />)} />

                  {/* Engineer tablet flow (Phase 5) */}
                  <Route path="my/jobs" element={R("job.execute", <EngineerJobsList />)} />
                  <Route path="my/jobs/:id" element={R("job.execute", <EngineerJobDetail />)} />
                  <Route path="my/jobs/:id/report" element={R("job.execute", <JobReportPage scope="engineer" />)} />

                  {/* Scheduling (Phase 7) */}
                  <Route path="scheduling/due" element={R("schedule.read", <DueSoonPage />)} />
                  <Route path="scheduling/call-to-book" element={R("schedule.read", <CallToBookPage />)} />
                  <Route path="scheduling/schedules" element={R("schedule.read", <ServiceSchedulesPage />)} />
                  <Route path="scheduling/schedules/new" element={R("schedule.write", <ServiceScheduleForm />)} />
                  <Route path="scheduling/schedules/:id/edit" element={R("schedule.write", <ServiceScheduleForm />)} />

                  {/* Reports (Phase 8) */}
                  <Route path="reports/engineers" element={R("report.read", <EngineersReport />)} />
                  <Route path="reports/time-on-site" element={R("report.read", <TimeOnSiteReport />)} />
                  <Route path="reports/actions-outstanding" element={R("report.read", <ActionsOutstandingReport />)} />
                  <Route path="reports/customer-history" element={R("report.read", <CustomerHistoryReport />)} />
                  <Route path="reports/machines-frequency" element={R("report.read", <MachinesFrequencyReport />)} />

                  <Route path="settings" element={<Settings />} />
                  <Route path="settings/company" element={<CompanyProfile />} />
                  <Route path="settings/templates" element={R("settings.templates.read", <TemplatesSettings />)} />
                  <Route path="settings/smtp" element={R("settings.smtp.read", <SmtpSettings />)} />
                  <Route path="settings/roles" element={R("settings.roles.read", <RolesList />)} />
                  <Route path="settings/roles/new" element={R("settings.roles.write", <RoleEdit mode="create" />)} />
                  <Route path="settings/roles/:roleId" element={R("settings.roles.read", <RoleEdit mode="edit" />)} />
                  <Route path="settings/scheduling" element={R("settings.scheduling.read", <SchedulingDefaults />)} />
                  <Route path="settings/checklist-defaults" element={R("settings.checklist.read", <ChecklistDefaults />)} />
                  <Route path="settings/security" element={<SecurityEvents />} />
                  <Route path="settings/branding" element={R("settings.branding.write", <Branding />)} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="bottom-right" />
            </AuthProvider>
            </CompanyProvider>
          </BrowserRouter>
        </TooltipProvider>
      </StylingProvider>
    </ThemeProvider>
  );
}
